/**
 * "Document model" do template Relatório de Manutenção SDAI — mesma fonte de
 * dados usada por `sections/Content.js` (PDF), extraída para um array de
 * blocos tipados e independente de React/JSX, consumido por
 * `_shared/generateDocx.js` para gerar o .docx.
 *
 * O mais complexo dos 5: tem subseções fixas (3.1-3.4, sem dados de reply) e
 * subseções dinâmicas (5.x, uma por pergunta de manutenção preventiva
 * respondida) — mantidas na mesma ordem/condição de `Content.js`.
 */
import { findReplyByQuestionId } from '../../_helpers/pdfHelpers'
import { questionBlocks, toImages } from '../../_shared/blockHelpers'
import { MONTHS } from '../../constants/dates'

const CHECKLIST_SECTIONS = [
  { id: 'central-incendio', title: '3.1. Central de Incêndio', items: ['Inspeção Visual', 'Limpeza da Central', 'Reaperto de Bornes e Conexões', 'Backup do sistema'] },
  {
    id: 'detectores-fumaca',
    title: '3.2. Detectores de Fumaça',
    items: [
      'Inspeção Visual',
      'Analise se o equipamento está funcionando',
      'Teste com Gás de Fumaça para Detectores de fumaça',
      'Teste com Soprador Térmico para Detectores de Temperatura',
      'Confirmação do Alarme de incêndio na Central'
    ]
  },
  {
    id: 'acionadores-manuais',
    title: '3.3. Acionadores Manuais',
    items: ['Inspeção Visual', 'Analise se o equipamento está funcionando', 'Teste de Acionamento do Equipamento', 'Confirmação do Alarme de incêndio na Central']
  },
  {
    id: 'sirenes-modulo-comando',
    title: '3.4. Sirenes e Módulo de Comando',
    items: ['Inspeção Visual', 'Analise se o equipamento está funcionando', 'Teste de acionamento de Sirene']
  }
]

const optionalQuestionBlocks = question => (question?.question?.name ? questionBlocks(question) : [])

const dynamicMaintenanceValue = question => {
  if (Array.isArray(question?.value)) {
    return question.value
      .map(item => item.name)
      .filter(Boolean)
      .join(', ')
  }
  if (typeof question?.value === 'boolean') return question.value ? 'Sim' : 'Não'
  return question?.value ?? 'N/A'
}

export const buildDocumentModel = serviceOrder => {
  const { replies, place } = serviceOrder || {}

  const question548 = findReplyByQuestionId(replies, 548)
  const question549 = findReplyByQuestionId(replies, 549)
  const question550 = findReplyByQuestionId(replies, 550)
  const question551 = findReplyByQuestionId(replies, 551)
  const question567 = findReplyByQuestionId(replies, 567)
  const question552 = findReplyByQuestionId(replies, 552)
  const question556 = findReplyByQuestionId(replies, 556)
  const question562 = findReplyByQuestionId(replies, 562)
  const question566 = findReplyByQuestionId(replies, 566)
  const question554 = findReplyByQuestionId(replies, 554)
  const question568 = findReplyByQuestionId(replies, 568)
  const question569 = findReplyByQuestionId(replies, 569)
  const question570 = findReplyByQuestionId(replies, 570)
  const question555 = findReplyByQuestionId(replies, 555)
  const question571 = findReplyByQuestionId(replies, 571)
  const question572 = findReplyByQuestionId(replies, 572)

  const currentMonth = new Date(question572?.value + 'T00:00:00').getMonth()
  const currentYear = new Date(question572?.value + 'T00:00:00').getFullYear()

  const maintenanceQuestions = [
    { id: '556', data: question556 },
    { id: '562', data: question562 },
    { id: '568', data: question568 },
    { id: '569', data: question569 },
    { id: '566', data: question566 },
    { id: '554', data: question554 }
  ].filter(q => q.data?.question?.name)

  const blocks = [
    {
      type: 'coverPage',
      lines: [`Manutenção Corretiva do Sistema de Detecção e Alarme de Incêndio – (SDAI) ${place?.resume || ''} - ${MONTHS[currentMonth]?.toUpperCase()} - ${currentYear}`]
    },
    { type: 'pageBreak' },
    { type: 'toc' },
    { type: 'pageBreak' },

    { type: 'heading', level: 1, text: '1. Introdução' },
    {
      type: 'paragraph',
      text: 'Este Relatório tem como objetivo expressar as atividades realizadas pela empresa Easy Solutions, bem como as os problemas encontrados, correções realizadas, visando informar sempre as melhorias cabíveis ao sistema de acordo com a necessidade de cliente e atender a NBR 17240-2010 (Sistema de Detecção e Alarme de Incêndio, Projeto Instalação, Comissionamento e Manutenção do Sistema).'
    },

    { type: 'heading', level: 1, text: '2. Ferramentas utilizadas para Testar Detectores', pageBreakBefore: true },
    {
      type: 'paragraph',
      text: 'A Easy Solutions possui os Kits para testar Detectores de fumaça e temperatura, Solo 100, equipamentos que atendem as Normas Internacionais de Fire Alarm, conforme figura abaixo.'
    },
    { type: 'imageGrid', images: [{ src: '/pdfs/easysolutions/fire_alarm.png', description: 'Figura 1 - Alarme de Incêndio' }] },

    { type: 'heading', level: 1, text: '3. Serviços Realizados', pageBreakBefore: true },
    ...CHECKLIST_SECTIONS.flatMap(section => [
      { type: 'heading', level: 2, text: section.title },
      { type: 'list', items: section.items }
    ]),

    { type: 'heading', level: 1, text: '4. Status da Central', pageBreakBefore: true },
    ...optionalQuestionBlocks(question548),
    ...optionalQuestionBlocks(question549),
    ...optionalQuestionBlocks(question550),
    ...optionalQuestionBlocks(question551),
    ...optionalQuestionBlocks(question567),
    ...optionalQuestionBlocks(question552),

    { type: 'heading', level: 1, text: '5. Manutenção preventiva realizada', pageBreakBefore: true },
    ...maintenanceQuestions.flatMap((q, index) => {
      const value = dynamicMaintenanceValue(q.data)
      const notes = q.data.notes ? `\nObservações: ${q.data.notes}` : ''
      const files = Array.isArray(q.data.value) ? q.data.value.filter(item => item?.src) : q.data.documents ?? []

      return [
        { type: 'heading', level: 2, text: `5.${index + 1}. ${q.data.question.name}`, pageBreakBefore: index > 0 },
        { type: 'paragraph', text: `${value}${notes}` },
        ...(files.length > 0 ? [{ type: 'imageGrid', images: toImages(files) }] : [])
      ]
    }),

    { type: 'heading', level: 1, text: '6. Conclusão', pageBreakBefore: true },
    ...(question570?.question?.name
      ? [{ type: 'keyValue', label: question570.question.name, value: Array.isArray(question570?.value) ? '' : question570?.value }]
      : []),
    ...optionalQuestionBlocks(question555),

    { type: 'paragraph', text: '', indent: false },
    ...(question571?.question?.name ? [{ type: 'paragraph', text: Array.isArray(question571?.value) ? '' : question571?.value, bold: true, italic: true, indent: false }] : []),
    ...(question572?.question?.name
      ? [
          {
            type: 'paragraph',
            text: Array.isArray(question572?.value) ? '' : new Date(question572?.value + 'T00:00:00').toLocaleDateString('pt-BR'),
            bold: true,
            italic: true,
            indent: false
          }
        ]
      : [])
  ]

  return {
    title: `Relatório de Manutenção SDAI ${place?.resume || ''} - ${MONTHS[currentMonth]} ${currentYear}`,
    blocks
  }
}

export default buildDocumentModel
